import { Menu, X } from 'lucide-react';
import { useState } from 'react';

import { localePath, parseLocalePath, useI18n, type AlternatePaths } from '../i18n';
import { cn } from '../lib/cn';
import { useData } from '../lib';
import { Button, Container } from '../ui';
import { BrandLogo } from './BrandLogo';
import { LanguageSwitcher } from './LanguageSwitcher';
import { PRIMARY_NAV } from './navigation';

export interface HeaderProps {
  currentUrl: string;
}

/**
 * The one field this header reads off the current page's own data, loosely
 * typed on purpose: `Header` is shared by every public route, most of which
 * have nothing to do with `alternates` at all, and it has no business
 * knowing which page-data shape it is looking at. A page whose `+data.ts`
 * never sets this field renders exactly as before — see `AlternatePaths`'s
 * doc comment for why that is the correct default rather than a gap.
 */
interface PageDataWithAlternates {
  alternates?: AlternatePaths;
}

export function Header({ currentUrl }: HeaderProps) {
  const { locale, t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const alternatePaths = useData<PageDataWithAlternates | undefined>()?.alternates;

  const { pathname } = parseLocalePath(currentUrl.split('?')[0] ?? '/');

  /*
   * The header is Paper on every public page, with the logo in Ink and the
   * Amber carry in its mark. The call to action is an Ink pill: the Amber is
   * spent on the page itself, not on chrome.
   */
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-bg">
      <Container>
        <div className="flex h-19 items-center justify-between gap-4">
          <a href={localePath(locale)} className="shrink-0 text-text">
            {/*
              The lockup is one inline SVG that names itself, so the link is
              named "Bitwize" without a second label. It never mirrors, in
              Arabic either — the symbol is a construction, not a direction.
            */}
            <BrandLogo className="h-7 w-auto" />
          </a>

          <nav className="hidden items-center gap-1 md:flex" aria-label={t('nav.aria.primary')}>
            {PRIMARY_NAV.map((item) => {
              const isCurrent = pathname === item.path || pathname.startsWith(`${item.path}/`);

              return (
                <a
                  key={item.path}
                  href={localePath(locale, item.path)}
                  aria-current={isCurrent ? 'page' : undefined}
                  className={cn(
                    'rounded-full px-4 py-2 text-sm font-medium transition-colors',
                    isCurrent
                      ? 'bg-selected text-selected-fg'
                      : 'text-text-muted hover:bg-surface-hover hover:text-text',
                  )}
                >
                  {t(item.labelKey)}
                </a>
              );
            })}
          </nav>

          {/*
            No theme control here. The public site is light, with Ink bands
            for the closing call to action and the footer (see `PublicLayout`),
            so a light/dark/system menu would be a control that changes
            nothing — and `ThemeProvider` is mounted disabled on these routes
            for the same reason. `ThemeToggle` still ships: `AdminLayout`
            mounts it, and the admin is genuinely themeable.
          */}
          {/*
            `gap-2`, not `gap-1`: on a phone this row is the language
            switcher immediately followed by the menu button, and 4px
            between two adjacent controls is a mis-tap waiting to happen.
          */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher currentUrl={currentUrl} alternatePaths={alternatePaths} />

            {/*
              The standing call to action, in Ink — the Amber carry is spent
              once per view, on the page's own primary action, not on chrome.
              Hidden below `md`, where the row is already carrying a hamburger
              and the language control, and the same link sits one tap away
              inside the menu.
            */}
            <Button asChild size="sm" className="ms-2 hidden md:inline-flex">
              <a href={localePath(locale, '/contact')}>{t('common.home.startProject')}</a>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              aria-expanded={menuOpen}
              aria-controls="primary-navigation"
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? (
                <X className="size-5" aria-hidden="true" />
              ) : (
                <Menu className="size-5" aria-hidden="true" />
              )}
              <span className="hs-sr-only">
                {menuOpen ? t('nav.aria.closeMenu') : t('nav.aria.openMenu')}
              </span>
            </Button>
          </div>
        </div>
      </Container>

      {/*
        Rendered in the DOM regardless of state and hidden with `hidden`, so
        that the navigation is present in the server HTML for a crawler and for
        a visitor without JavaScript.
      */}
      <div id="primary-navigation" hidden={!menuOpen} className="border-t border-border md:hidden">
        <Container>
          <nav className="flex flex-col py-2" aria-label={t('nav.aria.primary')}>
            {PRIMARY_NAV.map((item) => (
              <a
                key={item.path}
                href={localePath(locale, item.path)}
                className="rounded-sm px-3 py-3 text-base text-text hover:bg-surface-hover"
              >
                {t(item.labelKey)}
              </a>
            ))}
            <Button asChild size="lg" className="mt-2 mb-3">
              <a href={localePath(locale, '/contact')}>{t('common.home.startProject')}</a>
            </Button>
          </nav>
        </Container>
      </div>
    </header>
  );
}
